import { Select, Tag, Checkbox, Radio } from 'antd';
import type { QuestionItem, AnswerValue, SelectConfig } from '@/types/questionnaire';

interface SelectQuestionProps {
  question: QuestionItem;
  value?: AnswerValue;
  readOnly?: boolean;
  onChange?: (value: AnswerValue) => void;
}

const SelectQuestion = ({ question, value, readOnly = false, onChange }: SelectQuestionProps) => {
  const config = question.config as SelectConfig;
  const { mode, options, searchable = false, placeholder } = config;

  // 判断选项数量是否小于等于5
  const useInlineOptions = options.length <= 5;

  const handleChange = (val: string | string[] | number | number[]) => {
    if (onChange) {
      onChange(val as AnswerValue);
    }
  };

  // 只读模式：显示选中的值
  if (readOnly) {
    if (mode === 'multiple') {
      const selectedValues = Array.isArray(value) ? (value as Array<string | number>) : [];
      const selectedOptions = options.filter((opt) =>
        selectedValues.includes(opt.value as string | number),
      );
      if (selectedOptions.length === 0) {
        return <span className="text-slate-400">未选择</span>;
      }
      return (
        <div className="flex flex-wrap gap-2">
          {selectedOptions.map((opt) => (
            <Tag key={opt.value} color="geekblue">
              {opt.label}
            </Tag>
          ))}
        </div>
      );
    } else {
      const selectedOption = options.find((opt) => opt.value === value);
      if (!selectedOption) {
        return <span className="text-slate-400">未选择</span>;
      }
      return <span className="text-slate-700">{selectedOption.label}</span>;
    }
  }

  // 编辑模式：根据选项数量决定使用哪种组件
  if (mode === 'multiple') {
    // 多选模式
    if (useInlineOptions) {
      // 选项 <= 5 个，使用 Checkbox.Group
      const checkboxValue = Array.isArray(value) ? (value as Array<string | number>) : [];
      return (
        <Checkbox.Group
          value={checkboxValue}
          onChange={(checkedValues) => handleChange(checkedValues as string[] | number[])}
          className="w-full flex flex-col gap-2"
        >
          {options.map((opt) => (
            <Checkbox key={String(opt.value)} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </Checkbox>
          ))}
        </Checkbox.Group>
      );
    } else {
      // 选项 > 5 个，使用 Select
      return (
        <Select
          mode="multiple"
          value={Array.isArray(value) ? value : []}
          options={options.map((opt) => ({
            value: opt.value,
            label: opt.label,
            disabled: opt.disabled,
          }))}
          placeholder={placeholder || '请选择'}
          showSearch={searchable}
          onChange={handleChange}
          className="w-full"
          allowClear
        />
      );
    }
  } else {
    // 单选模式
    if (useInlineOptions) {
      // 选项 <= 5 个，使用 Radio.Group
      return (
        <Radio.Group
          value={value as string | number | undefined}
          onChange={(e) => handleChange(e.target.value)}
          className="w-full flex flex-col gap-2"
        >
          {options.map((opt) => (
            <Radio key={String(opt.value)} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </Radio>
          ))}
        </Radio.Group>
      );
    } else {
      // 选项 > 5 个，使用 Select
      return (
        <Select
          value={value as string | number | undefined}
          options={options.map((opt) => ({
            value: opt.value,
            label: opt.label,
            disabled: opt.disabled,
          }))}
          placeholder={placeholder || '请选择'}
          showSearch={searchable}
          onChange={handleChange}
          className="w-full"
          allowClear
        />
      );
    }
  }
};

export default SelectQuestion;
